// src/pages/Appli/AppliAdmin/VisitorCenter/hooks/useVisitorConversations.js

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getVisitorConversationById,
  getVisitorConversations,
  markConversationAsReadByAdmin,
  sendAdminConversationMessage,
  updateVisitorConversation,
} from "./../../../../../services/visitorConversationServices";

export default function useVisitorConversations({
  enabled = false,
  pollMs = 10000,
} = {}) {
  const [rows, setRows] = useState([]);
  const [selectedConversationId, setSelectedConversationId] =
    useState(null);

  const [selectedConversation, setSelectedConversation] =
    useState(null);

  const [status, setStatus] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] =
    useState(false);

  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const refresh = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);

      setErrorMessage("");

      const response = await getVisitorConversations({
        page,
        limit: 30,
        status,
        unread: onlyUnread ? "true" : undefined,
      });

      const nextRows = response?.data || [];

      setRows(nextRows);
      setTotalPages(response?.totalPages || 1);

      setSelectedConversationId((currentId) => {
        if (currentId) return currentId;

        return nextRows[0]?.id || null;
      });
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error ||
          "Impossible de récupérer les conversations.",
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, [onlyUnread, page, status]);

  const selectConversation = useCallback(
    async (conversationId) => {
      if (!conversationId) {
        setSelectedConversationId(null);
        setSelectedConversation(null);
        return;
      }

      try {
        setSelectedConversationId(conversationId);
        setDetailsLoading(true);

        await markConversationAsReadByAdmin(
          conversationId,
        );

        const response =
          await getVisitorConversationById(
            conversationId,
          );

        setSelectedConversation(response?.data || null);
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.error ||
            "Impossible de charger la conversation.",
        );
      } finally {
        setDetailsLoading(false);
      }
    },
    [],
  );

  const sendMessage = useCallback(
    async (message) => {
      if (!selectedConversationId || !message?.trim()) {
        return;
      }

      try {
        setSending(true);

        await sendAdminConversationMessage(
          selectedConversationId,
          message.trim(),
        );

        await selectConversation(
          selectedConversationId,
        );

        await refresh({ silent: true });
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.error ||
            "Impossible d’envoyer le message.",
        );
      } finally {
        setSending(false);
      }
    },
    [
      refresh,
      selectConversation,
      selectedConversationId,
    ],
  );

  const changeConversationStatus = useCallback(
    async (nextStatus) => {
      if (!selectedConversationId) return;

      try {
        await updateVisitorConversation(
          selectedConversationId,
          {
            status: nextStatus,
          },
        );

        await selectConversation(
          selectedConversationId,
        );

        await refresh({ silent: true });
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.error ||
            "Impossible de modifier la conversation.",
        );
      }
    },
    [
      refresh,
      selectConversation,
      selectedConversationId,
    ],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    refresh();

    const intervalId = window.setInterval(() => {
      refresh({ silent: true });
    }, pollMs);

    return () => window.clearInterval(intervalId);
  }, [enabled, pollMs, refresh]);

  useEffect(() => {
    if (!enabled || !selectedConversationId) return;

    selectConversation(selectedConversationId);
  }, [
    enabled,
    selectConversation,
    selectedConversationId,
  ]);

  return {
    rows,

    selectedConversationId,
    selectedConversation,

    status,
    onlyUnread,

    page,
    totalPages,

    loading,
    detailsLoading,
    sending,
    errorMessage,

    setStatus,
    setOnlyUnread,
    setPage,

    refresh,
    selectConversation,
    sendMessage,
    changeConversationStatus,
  };
}